import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentLogoUrl } from '../lib/brandingConfig';

/**
 * Shared Logo component with automatic seasonal switching
 * 
 * Uses brandingConfig.js to determine the active logo based on:
 * 1. Admin force override (if enabled)
 * 2. Current date vs seasonal windows
 * 3. Fallback to default logo
 * 
 * @param {string} className - Additional CSS classes
 * @param {string} alt - Alt text (default: "Stock & Load")
 * @param {string} size - Preset size: 'sm' (h-12), 'md' (h-24), 'lg' (h-36), or custom via className
 */
export const Logo = ({ 
  className = '', 
  alt = 'Stock & Load',
  size = 'md',
  'data-testid': dataTestId = 'app-logo'
}) => {
  // Try to get tenant timezone from auth context for accurate date calculation
  let tenantTimezone = null;
  try {
    // This will work when inside AuthProvider
    const auth = useAuth();
    tenantTimezone = auth?.user?.tenant_timezone || null;
  } catch {
    // Outside of AuthProvider (e.g., login page) - use local time
    tenantTimezone = null;
  }
  
  const logoUrl = getCurrentLogoUrl(tenantTimezone);
  
  // Size presets
  const sizeClasses = {
    sm: 'h-12 w-auto',
    md: 'h-24 w-auto',
    lg: 'h-[16.5rem] w-auto max-w-[90%]',
    sidebar: 'h-20 w-auto max-w-full object-contain opacity-80',
    'mobile-header': 'h-12 w-auto object-contain',
  };
  
  const sizeClass = sizeClasses[size] || '';
  
  // The 'lg' logo PNG has ~30% top and ~9% bottom transparent padding baked in.
  // Wrap in a cropping container to eliminate that dead space cleanly.
  if (size === 'lg') {
    return (
      <div className="overflow-hidden h-[9rem] flex items-center justify-center">
        <img
          src={logoUrl}
          alt={alt}
          className="h-[16.5rem] w-auto max-w-[90%] object-contain scale-110"
          data-testid={dataTestId}
        />
      </div>
    );
  }

  // Sidebar: same PNG whitespace issue — crop with overflow-hidden container
  if (size === 'sidebar') {
    return (
      <div className={`overflow-hidden h-14 flex items-center justify-center ${className}`.trim()}>
        <img
          src={logoUrl}
          alt={alt}
          className="h-[7.5rem] w-auto max-w-full object-contain opacity-80"
          data-testid={dataTestId}
        />
      </div>
    );
  }

  // Header bar: compact logo for top navigation bar
  if (size === 'header') {
    return (
      <div className={`overflow-hidden h-10 flex items-center shrink-0 ${className}`.trim()}>
        <img
          src={logoUrl}
          alt={alt}
          className="h-[5.5rem] w-auto object-contain opacity-90"
          data-testid={dataTestId}
        />
      </div>
    );
  }

  // Collapsed sidebar: small centered icon
  if (size === 'icon-only') {
    return (
      <div className={`overflow-hidden h-8 w-8 flex items-center justify-center ${className}`.trim()}>
        <img
          src={logoUrl}
          alt={alt}
          className="h-[5rem] w-auto object-contain opacity-70"
          data-testid={dataTestId}
        />
      </div>
    );
  }

  return (
    <img 
      src={logoUrl}
      alt={alt}
      className={`${sizeClass} ${className}`.trim()}
      data-testid={dataTestId}
    />
  );
};

/**
 * Logo component for use outside AuthProvider (login, signup, etc.)
 * Does not attempt to use useAuth hook
 */
export const LogoPublic = ({ 
  className = '', 
  alt = 'Stock & Load',
  size = 'md',
  'data-testid': dataTestId = 'app-logo'
}) => {
  // Use local time since we don't have tenant context
  const logoUrl = getCurrentLogoUrl(null);
  
  // Size presets
  const sizeClasses = {
    sm: 'h-12 w-auto',
    md: 'h-24 w-auto',
    lg: 'h-[16.5rem] w-auto max-w-[90%]',
    sidebar: 'h-20 w-auto max-w-full object-contain opacity-80',
    'mobile-header': 'h-12 w-auto object-contain',
  };
  
  const sizeClass = sizeClasses[size] || '';

  if (size === 'lg') {
    return (
      <div className={`overflow-hidden h-[9rem] flex items-center justify-center ${className}`.trim()}>
        <img
          src={logoUrl}
          alt={alt}
          className="h-[16.5rem] w-auto max-w-[90%] object-contain scale-110"
          data-testid={dataTestId}
        />
      </div>
    );
  }

  if (size === 'sidebar') {
    return (
      <div className={`overflow-hidden h-14 flex items-center justify-center ${className}`.trim()}>
        <img
          src={logoUrl}
          alt={alt}
          className="h-[7.5rem] w-auto max-w-full object-contain opacity-80"
          data-testid={dataTestId}
        />
      </div>
    );
  }
  
  return (
    <img 
      src={logoUrl}
      alt={alt}
      className={`${sizeClass} ${className}`.trim()}
      data-testid={dataTestId}
    />
  );
};

export default Logo;
